import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray, ResponseObject } from '@/types/shared';
import { ticketKeys, type Ticket } from '@/api/tickets';

/**
 * Tickets workflow mutations + lookup queries.
 * Workflow actions: PUT /tickets/:id/<action>
 * Assign: POST /tickets/:id/assign-tech, POST /tickets/bulk-assign-tech
 * Lookups: ticket_request, ticket_type, customer services, feedbacks
 */

/* ── Lookup types (verbatim from legacy types/response.ts) ──────────────── */

export interface TicketRequest {
  ticket_request_id: number;
  ticket_request_title: string;
  ticket_request_key: string;
}

export interface TicketType {
  ticket_type_id: number;
  ticket_type_title: string;
  ticket_type_key: string;
  ticket_type_sample_content: string;
  ticket_type_time: string;
  ticket_type_time_format: string;
  ticket_request_id: number;
  ticket_priority: string;
  ticket_type_group?: string;
}

export interface CustomerService {
  service_id: number;
  service_name: string;
  product_group_name: string;
  service_status: string;
  customer_id: number;
  service_start_date: string;
  service_stopped_date: string;
  is_poc?: boolean;
  /* Present in the live response; the create form auto-fills assignees from these. */
  cs_assignees?: number[];
  sale_assignees?: number[];
}

export interface CustomerServiceInteresting {
  label: string;
  value: string;
  cs_assignees?: number[];
  sale_assignees?: number[];
}

export interface TicketCustomerServices {
  services: CustomerService[];
  service_interestings: CustomerServiceInteresting[];
}

export interface TicketFeedback {
  feedback_id: number;
  ticket_id: number;
  token_id?: number;
  rating: number;
  /** Dual-rating fields (api-v4 new system; may be absent on legacy records). */
  rating_support_quality?: number;
  rating_staff_attitude?: number;
  feedback_text?: string;
  feedback_by_name?: string;
  feedback_by_email?: string;
  feedback_by_phone?: string;
  created_at: string;
}

export interface FeedbackStats {
  average_rating: number;
  /** Extended stats fields (api-v4; present when backend sends them). */
  average_rating_support_quality?: number;
  average_rating_staff_attitude?: number;
  total_feedbacks: number;
}

/** Non-standard response shape for `GET /tickets/:id/feedbacks`. */
export interface TicketFeedbacksResponse {
  message: string;
  feedbacks: TicketFeedback[];
  stats: FeedbackStats;
}

/**
 * Feedback token — returned by `GET /tickets/:id/feedback-tokens`.
 * Shape matches `GetTicketFeedbackTokensByTicketIDRow` from api-v4 sqlc.
 */
export interface TicketFeedbackToken {
  token_id: number;
  ticket_id: number;
  token: string;
  expires_at: string | null;
  created_at: string | null;
  created_by: number | null;
  status: string | null; // "active" | "used" | "revoked"
  used_at: string | null;
  revoked_at: string | null;
  recipient_name: string | null;
  recipient_email: string | null;
  recipient_phone: string | null;
  created_by_name: string | null;
}

export interface CreateFeedbackTokenPayload {
  recipient_name?: string;
  recipient_email?: string;
  recipient_phone?: string;
}

/** Response shape for `POST /tickets/:id/feedback-token`. */
export interface FeedbackTokenCreatedResponse {
  message: string;
  result: {
    token_id: number;
    token: string;
    expires_at: string | null;
    feedback_url: string;
    recipient_name?: string;
  };
}

/* ── Workflow mutation payloads ──────────────────────────────────────────── */

export interface StartTicketPayload {
  ticket_id: number;
  ticket_time_response?: string | null;
  ticket_technical_assignees?: number[];
  reason?: string;
}

export interface RollbackTicketPayload {
  ticket_id: number;
  content: string;
}

export interface EscalateTicketPayload {
  ticket_id: number;
  reason: string;
  escalate_to?: number[];
}

export interface ConvertIncidentPayload {
  ticket_id: number;
  content: string;
}

export interface LinkedTicketPayload {
  ticket_id: number;
  ticket_groups: string[];
}

export interface AssignTicketPayload {
  ticket_id: number;
  technical_assignees: number[];
  reason?: string;
}

export interface BulkAssignTicketsPayload {
  ticket_ids: number[];
  technical_assignees: number[];
  reason?: string;
}

/* ── Query keys ──────────────────────────────────────────────────────────── */

export const ticketWorkflowKeys = {
  ticketRequests: ['tickets', 'ticket_request'] as const,
  ticketTypes: (group?: string) => ['tickets', 'ticket_type', group] as const,
  customerServices: (customerId: number, journeyId?: number) =>
    ['tickets', 'customer-services', customerId, journeyId] as const,
  feedbacks: (ticketId: number) => ['tickets', ticketId, 'feedbacks'] as const,
  feedbackTokens: (ticketId: number) => ['tickets', ticketId, 'feedback-tokens'] as const,
};

/* ── Lookups ─────────────────────────────────────────────────────────────── */

/** `GET /tickets/ticket_request` — all request categories. */
export function useTicketRequests() {
  return useQuery({
    queryKey: ticketWorkflowKeys.ticketRequests,
    queryFn: async (): Promise<ResponseArray<TicketRequest>> => {
      const res = await dataCentralApi.get<ResponseArray<TicketRequest>>(
        '/tickets/ticket_request',
      );
      return res.data;
    },
  });
}

/** `GET /tickets/ticket_type?ticket_type_group=` — ticket types, optionally filtered by group. */
export function useTicketTypes(ticketTypeGroup?: string) {
  return useQuery({
    queryKey: ticketWorkflowKeys.ticketTypes(ticketTypeGroup),
    queryFn: async (): Promise<ResponseArray<TicketType>> => {
      const query = ticketTypeGroup
        ? `?${qs.stringify({ ticket_type_group: ticketTypeGroup })}`
        : '';
      const res = await dataCentralApi.get<ResponseArray<TicketType>>(
        `/tickets/ticket_type${query}`,
      );
      return res.data;
    },
  });
}

/**
 * `GET /tickets/customers/:customerID/services?journey_id=`
 * Returns `{ result: { services, service_interestings } }` — note `result` (object), not `results`.
 */
export function useTicketCustomerServices(customerId: number, journeyId?: number) {
  return useQuery({
    queryKey: ticketWorkflowKeys.customerServices(customerId, journeyId),
    enabled: !!customerId,
    queryFn: async (): Promise<ResponseObject<TicketCustomerServices>> => {
      const query = journeyId ? `?journey_id=${journeyId}` : '';
      const res = await dataCentralApi.get<ResponseObject<TicketCustomerServices>>(
        `/tickets/customers/${customerId}/services${query}`,
      );
      return res.data;
    },
  });
}

/**
 * `GET /tickets/:id/feedbacks`
 * Non-standard wrapper: `{ message, feedbacks, stats }` — typed separately.
 */
export function useTicketFeedbacks(ticketId: number) {
  return useQuery({
    queryKey: ticketWorkflowKeys.feedbacks(ticketId),
    enabled: !!ticketId,
    queryFn: async (): Promise<TicketFeedbacksResponse> => {
      const res = await dataCentralApi.get<TicketFeedbacksResponse>(
        `/tickets/${ticketId}/feedbacks`,
      );
      return res.data;
    },
  });
}

/* ── Workflow mutations ───────────────────────────────────────────────────── */

/** `PUT /tickets/:id/start` */
export function useStartTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ticket_id,
      ticket_time_response,
      ticket_technical_assignees,
      reason,
    }: StartTicketPayload): Promise<ResponseObject<Ticket>> => {
      const res = await dataCentralApi.put<ResponseObject<Ticket>>(
        `/tickets/${ticket_id}/start`,
        { ticket_time_response, ticket_technical_assignees, reason },
      );
      return res.data;
    },
    onSuccess: (_data, { ticket_id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticket_id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

/** `PUT /tickets/:id/resolved` */
export function useResolveTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticket_id }: { ticket_id: number }): Promise<ResponseObject<Ticket>> => {
      const res = await dataCentralApi.put<ResponseObject<Ticket>>(
        `/tickets/${ticket_id}/resolved`,
      );
      return res.data;
    },
    onSuccess: (_data, { ticket_id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticket_id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

/** `PUT /tickets/:id/rollback` */
export function useRollbackTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ticket_id,
      content,
    }: RollbackTicketPayload): Promise<ResponseObject<Ticket>> => {
      const res = await dataCentralApi.put<ResponseObject<Ticket>>(
        `/tickets/${ticket_id}/rollback`,
        { content },
      );
      return res.data;
    },
    onSuccess: (_data, { ticket_id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticket_id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

/** `PUT /tickets/:id/closed` */
export function useCloseTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ticket_id }: { ticket_id: number }): Promise<ResponseObject<Ticket>> => {
      const res = await dataCentralApi.put<ResponseObject<Ticket>>(
        `/tickets/${ticket_id}/closed`,
      );
      return res.data;
    },
    onSuccess: (_data, { ticket_id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticket_id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

/** `PUT /tickets/:id/escalate` */
export function useEscalateTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ticket_id,
      reason,
      escalate_to,
    }: EscalateTicketPayload): Promise<ResponseObject<Ticket>> => {
      const res = await dataCentralApi.put<ResponseObject<Ticket>>(
        `/tickets/${ticket_id}/escalate`,
        { reason, escalate_to },
      );
      return res.data;
    },
    onSuccess: (_data, { ticket_id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticket_id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

/** `PUT /tickets/:id/convert_incident_to_problem` */
export function useConvertIncidentToProblem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ticket_id,
      content,
    }: ConvertIncidentPayload): Promise<ResponseObject<Ticket>> => {
      const res = await dataCentralApi.put<ResponseObject<Ticket>>(
        `/tickets/${ticket_id}/convert_incident_to_problem`,
        { content },
      );
      return res.data;
    },
    onSuccess: (_data, { ticket_id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticket_id) });
    },
  });
}

/** `PUT /tickets/:id/linked` — link related tickets by ticket_groups (string IDs). */
export function useLinkedTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ticket_id,
      ticket_groups,
    }: LinkedTicketPayload): Promise<ResponseObject<Ticket>> => {
      const res = await dataCentralApi.put<ResponseObject<Ticket>>(
        `/tickets/${ticket_id}/linked`,
        { ticket_groups },
      );
      return res.data;
    },
    onSuccess: (_data, { ticket_id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticket_id) });
    },
  });
}

/* ── Assign mutations ────────────────────────────────────────────────────── */

/** `POST /tickets/:id/assign-tech` */
export function useAssignTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ticket_id,
      technical_assignees,
      reason,
    }: AssignTicketPayload): Promise<ResponseObject<Ticket>> => {
      const res = await dataCentralApi.post<ResponseObject<Ticket>>(
        `/tickets/${ticket_id}/assign-tech`,
        { technical_assignees, reason },
      );
      return res.data;
    },
    onSuccess: (_data, { ticket_id }) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticket_id) });
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

/** `POST /tickets/bulk-assign-tech` */
export function useBulkAssignTickets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ticket_ids,
      technical_assignees,
      reason,
    }: BulkAssignTicketsPayload): Promise<ResponseObject<{ success: number; failed: number }>> => {
      const res = await dataCentralApi.post<
        ResponseObject<{ success: number; failed: number }>
      >('/tickets/bulk-assign-tech', { ticket_ids, technical_assignees, reason });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}

/* ── Feedback token hooks (api-v4 confirmed) ─────────────────────────────── */

/**
 * `GET /tickets/:ticketId/feedback-tokens`
 * Returns all tokens for a ticket (active, used, revoked).
 */
export function useTicketFeedbackTokens(ticketId: number) {
  return useQuery({
    queryKey: ticketWorkflowKeys.feedbackTokens(ticketId),
    enabled: !!ticketId,
    queryFn: async (): Promise<ResponseArray<TicketFeedbackToken>> => {
      const res = await dataCentralApi.get<ResponseArray<TicketFeedbackToken>>(
        `/tickets/${ticketId}/feedback-tokens`,
      );
      return res.data;
    },
  });
}

/**
 * `POST /tickets/:ticketId/feedback-token`
 * Creates a new feedback token. Returns `FeedbackTokenCreatedResponse` with the
 * token string and the constructed `feedback_url` from the backend.
 */
export function useCreateFeedbackToken(ticketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      payload: CreateFeedbackTokenPayload,
    ): Promise<FeedbackTokenCreatedResponse> => {
      const res = await dataCentralApi.post<FeedbackTokenCreatedResponse>(
        `/tickets/${ticketId}/feedback-token`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketWorkflowKeys.feedbackTokens(ticketId),
      });
    },
  });
}

/**
 * `DELETE /tickets/feedback-token/:tokenId`
 * Revokes a feedback token by setting its status to "revoked".
 */
export function useRevokeFeedbackToken(ticketId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tokenId: number): Promise<ResponseObject<unknown>> => {
      const res = await dataCentralApi.delete<ResponseObject<unknown>>(
        `/tickets/feedback-token/${tokenId}`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketWorkflowKeys.feedbackTokens(ticketId),
      });
    },
  });
}
